import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', path: '/dashboard', icon: '🏠' },
    { name: 'My Positions', path: '/positions', icon: '📊' },
    { name: 'APR Tracker', path: '/tracker', icon: '📈' },
    ...(isAdmin ? [
      { name: 'User Management', path: '/users', icon: '👨‍💼' },
      { name: 'User Logins', path: '/user-logins', icon: '🔐' }
    ] : []),
    { name: 'Profile Settings', path: '/profile', icon: '⚙️' }
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="layout">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <nav className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <img src="/Final2.png" alt="Sepori Capital" className="sidebar-logo mobile-sidebar-logo-hide" />
          <button 
            className="sidebar-close-button"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>
        
        <div className="sidebar-nav">
          {navigation.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-text">{item.name}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Header */}
        <header className="top-header">
          <div className="header-content">
            <div className="header-left">
              <button
                className="mobile-menu-button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                <svg className="hamburger-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <img 
                src="/Final2.png" 
                alt="Sepori Capital" 
                className="mobile-header-logo" 
              />
              <h2 className="page-title">
                {navigation.find(item => item.path === location.pathname)?.name || 'Dashboard'}
              </h2>
            </div>
            
            <div className="header-right">
              <div className="profile-dropdown">
                <button
                  className="profile-button"
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                >
                  <div className="profile-avatar">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <div className="profile-info-header">
                    <div className="profile-name">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="profile-email">{user?.email}</div>
                  </div>
                </button>
                
                {profileDropdownOpen && (
                  <>
                    <div 
                      className="dropdown-overlay"
                      onClick={() => setProfileDropdownOpen(false)}
                    />
                    <div className="profile-menu">
                      <Link 
                        to="/profile" 
                        className="profile-menu-item"
                        onClick={() => {
                          setProfileDropdownOpen(false);
                        }}
                      >
                        ⚙️ Profile Settings
                      </Link>
                      <button 
                        className="profile-menu-item logout"
                        onClick={() => {
                          logout();
                          setProfileDropdownOpen(false);
                        }}
                      >
                        🚪 Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content">
          {children}
        </main>
      </div>


      <style>{`
        .layout {
          display: flex;
          min-height: 100vh;
          background-color: #0a0e17;
        }

        @media (max-width: 768px) {
          .layout {
            overflow-x: hidden;
          }

          .main-content {
            overflow-x: hidden;
          }
        }

        .sidebar {
          width: 260px;
          background: linear-gradient(180deg, #1e293b 0%, #334155 100%);
          color: white;
          display: flex;
          flex-direction: column;
          position: fixed;
          height: 100vh;
          z-index: 1000;
        }

        .sidebar-header {
          padding: 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          height: 70px;
          box-sizing: border-box;
        }

        .sidebar-logo {
          width: 220px;
          height: auto;
          object-fit: contain;
        }

        .sidebar-header h1 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: white;
        }

        .sidebar-nav {
          flex: 1;
          padding: 20px 0;
        }

        .nav-item {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          transition: all 0.2s;
          border-left: 3px solid transparent;
        }

        .nav-item:hover {
          background-color: rgba(255, 255, 255, 0.1);
          color: white;
        }

        .nav-item.active {
          background-color: rgba(255, 255, 255, 0.15);
          color: white;
          border-left-color: #3b82f6;
        }

        .nav-icon {
          margin-right: 12px;
          font-size: 16px;
        }

        .nav-text {
          font-weight: 500;
        }

        .main-content {
          flex: 1;
          margin-left: 260px;
          background: #0a0e17;
          display: flex;
          flex-direction: column;
        }

        .top-header {
          background: #151b2b;
          border-bottom: 1px solid #1e2940;
          padding: 0 32px;
          height: 70px;
          display: flex;
          align-items: center;
          position: sticky;
          top: 0;
          z-index: 100;
          overflow: visible;
        }

        .header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        }

        .page-title {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
          color: #ffffff;
        }

        .profile-dropdown {
          position: relative;
          display: flex;
          align-items: center;
        }

        .profile-button {
          display: flex;
          align-items: center;
          gap: 12px;
          background: none;
          border: none;
          padding: 8px 12px;
          border-radius: 8px;
          cursor: pointer;
          transition: background-color 0.2s;
          flex-shrink: 0;
          height: 50px;
        }

        .profile-button:hover {
          background-color: rgba(255, 255, 255, 0.08);
        }

        .profile-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 14px;
        }

        .profile-info-header {
          text-align: left;
          display: flex;
          flex-direction: column;
          gap: 2px;
          justify-content: center;
          align-items: flex-start;
        }

        .profile-name {
          font-weight: 600;
          color: #ffffff;
          font-size: 14px;
          white-space: nowrap;
        }

        .profile-email {
          color: #a0aec0;
          font-size: 12px;
          white-space: nowrap;
        }


        .profile-menu {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background: #151b2b;
          border: 1px solid #1e2940;
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
          min-width: 200px;
          z-index: 1001;
          overflow: hidden;
        }

        .profile-menu-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          color: #ffffff;
          text-decoration: none;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
          transition: background-color 0.2s;
          font-size: 14px;
          pointer-events: auto;
          user-select: none;
        }

        .profile-menu-item:hover {
          background-color: rgba(59, 130, 246, 0.1);
        }

        .profile-menu-item.logout {
          color: #ef4444;
          border-top: 1px solid #1e2940;
        }

        .profile-menu-item.logout:hover {
          background-color: rgba(239, 68, 68, 0.1);
        }

        .page-content {
          flex: 1;
          padding: 32px;
          background-color: #0a0e17;
        }

        @media (max-width: 768px) {
          .page-content {
            padding: 12px;
          }
        }

        @media (max-width: 480px) {
          .page-content {
            padding: 8px;
          }
        }

        .dropdown-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 999;
          background: transparent;
        }

        .mobile-menu-overlay {
          display: none;
        }

        .mobile-menu-button {
          display: none;
        }

        .mobile-header-logo {
          display: none;
        }

        .mobile-sidebar-logo-hide {
          display: block;
        }

        .sidebar-close-button {
          display: none;
        }

        @media (max-width: 768px) {
          .header-content {
            justify-content: space-between;
          }

          .header-left {
            display: flex;
            align-items: center;
            gap: 12px;
            flex: 1;
            justify-content: center;
            position: relative;
          }

          .mobile-header-logo {
            display: block;
            height: 40px;
            width: auto;
            object-fit: contain;
          }

          .page-title {
            display: none;
          }

          .mobile-menu-button {
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            left: 0;
            background: none;
            border: none;
            color: #ffffff;
            padding: 8px;
            cursor: pointer;
            border-radius: 4px;
            transition: background-color 0.2s;
          }

          .mobile-menu-button:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }

          .hamburger-icon {
            width: 24px;
            height: 24px;
          }

          .header-right {
            flex-shrink: 0;
          }

          .top-header {
            padding: 0 16px;
            overflow-x: hidden;
          }

          .sidebar {
            transform: translateX(-100%);
            transition: transform 0.3s ease-in-out;
          }

          .sidebar.mobile-open {
            transform: translateX(0);
          }

          .main-content {
            margin-left: 0;
          }

          .mobile-menu-overlay {
            display: block;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 999;
          }

          .sidebar-close-button {
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.1);
            border: none;
            color: white;
            width: 32px;
            height: 32px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 20px;
            margin-left: auto;
            transition: background-color 0.2s;
          }

          .sidebar-close-button:hover {
            background-color: rgba(255, 255, 255, 0.2);
          }

          .sidebar-header {
            justify-content: flex-end;
            padding: 20px;
          }

          .mobile-sidebar-logo-hide {
            display: none;
          }

          .profile-button {
            padding: 4px 8px;
          }

          .profile-info-header {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .sidebar-header {
            justify-content: flex-end;
            padding: 20px;
          }

          .mobile-sidebar-logo-hide {
            display: none;
          }

          .top-header {
            padding: 0 8px;
          }

          .mobile-header-logo {
            height: 32px;
          }

          .header-left {
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
};
